import { useState, type ComponentType } from 'react'
import { SiteLayout } from '../components/SiteLayout'
import { blogPosts, contentPath, formatDate, type ContentEntry } from '../lib/content'
import { authorForByline, authorSlugPath } from '../lib/authors'
import { CommitCard } from '../components/pr7536-story/CommitCard'
import { STORY_SLUG } from '../components/pr7536-story/route'

/** Posts that draw their own featured card instead of the standard one. */
const CUSTOM_CARDS: Record<string, ComponentType<{ entry: ContentEntry }>> = {
  [STORY_SLUG]: CommitCard,
}

/** The six posts shown big at the top of /blog (All tab), in this order. Hand-picked:
 * the ones written with the most human back and forth. Swap one out only for a post
 * crafted with the same care, not for whatever is newest. */
const PINNED = [
  'introducing-instant-evals',
  'rebuilding-langwatch-in-51-days',
  'context-tax-when-to-compact',
  'claude-code-usage-tracking',
  'introducing-langy-your-automated-ai-engineer',
  'essential-llm-evaluation-metrics-for-ai-quality-control',
]

/** Canonical blog category taxonomy, in display order. Posts set one of these
 * via their `category` frontmatter; the filter bar is built from whichever of
 * these actually have posts. */
const CATEGORIES = [
  'Agents',
  'Integrations',
  'LLM Evals',
  'Product Releases',
  'Developer findings',
  'Voice AI',
  'Governance AI',
] as const

/** First ~110 chars of the intro, trimmed at a word boundary. */
function excerpt(s?: string): string {
  if (!s) return ''
  const clean = s.replace(/\s+/g, ' ').trim()
  if (clean.length <= 120) return clean
  return clean.slice(0, 117).replace(/\s+\S*$/, '') + '…'
}

function Kicker({ entry }: { entry: ContentEntry }) {
  if (!entry.meta.category) return null
  return (
    <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-500">
      {entry.meta.category}
    </span>
  )
}

/** Featured card: a real-photo cover when the post opts in via listImage, else
 * a bold typographic card that carries itself with no image. */
function FeaturedCard({ entry }: { entry: ContentEntry }) {
  const showImage = entry.meta.listImage && entry.meta.image
  const author = authorForByline(entry.meta.author)
  return (
    <div className="relative top-0 flex flex-col transition-[top] duration-300 ease-out hover:-top-1.5">
      {/* stretched link: the whole card is clickable, except the byline link below */}
      <a
        href={contentPath(entry)}
        aria-label={entry.meta.title}
        className="absolute inset-0 z-0"
      />
      {showImage ? (
        <div className="pointer-events-none mb-4 aspect-[16/10] overflow-hidden rounded-2xl border border-ink-100 bg-paper-soft">
          <img
            src={entry.meta.image}
            alt={entry.meta.title}
            loading="lazy"
            className="h-full w-full object-cover"
          />
        </div>
      ) : null}
      <div
        className={
          showImage
            ? 'pointer-events-none'
            : 'pointer-events-none relative flex flex-1 flex-col rounded-2xl border border-ink-100 bg-paper-soft p-6'
        }
      >
        <Kicker entry={entry} />
        <h2
          className={
            showImage
              ? 'mt-2 text-[19px] font-semibold leading-[1.3] text-ink-900'
              : 'display mt-3 text-[24px] leading-[1.15] text-ink-900 md:text-[27px]'
          }
        >
          {entry.meta.title}
        </h2>
        {!showImage && entry.meta.intro && (
          <p className="mt-3 flex-1 text-[14.5px] leading-[1.55] text-ink-600">
            {excerpt(entry.meta.intro)}
          </p>
        )}
        <div className="relative z-10 mt-3 text-[13px] text-ink-500">
          {author ? (
            <a
              href={authorSlugPath(author)}
              className="pointer-events-auto relative z-10 hover:text-ink-800 hover:underline"
            >
              {entry.meta.author}
            </a>
          ) : (
            entry.meta.author
          )}
          {entry.meta.author ? '  ·  ' : ''}
          {formatDate(entry.meta.date) || 'Date coming soon'}
        </div>
      </div>
    </div>
  )
}

/** Archive row: clean, scannable, no image. Scales to the full back catalogue.
 * Exported so AuthorPage can reuse the same row rendering for a per-author
 * post archive instead of inventing a second list treatment. */
export function ArchiveRow({
  entry,
  showAuthor = true,
}: {
  entry: ContentEntry
  /** Hide the author on the author page's own post list (it would be redundant). */
  showAuthor?: boolean
}) {
  const showImage = entry.meta.listImage && entry.meta.image
  const isDeveloperPost = /engineer|developer|code|sdk|framework|opentelemetry/i.test(
    `${entry.meta.category ?? ''} ${entry.meta.title}`,
  )
  const author = showAuthor ? authorForByline(entry.meta.author) : undefined
  return (
    <div className="relative top-0 flex items-center gap-5 border-b border-ink-100 py-5 transition-[top] duration-300 ease-out hover:-top-1.5">
      {/* stretched link: the whole row is clickable, except the author link below */}
      <a href={contentPath(entry)} aria-label={entry.meta.title} className="absolute inset-0 z-0" />
      <div className="pointer-events-none min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span className="text-[13px] text-ink-500">
            {formatDate(entry.meta.date) || 'Date coming soon'}
          </span>
          {entry.meta.category && (
            <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-400">
              {entry.meta.category}
            </span>
          )}
          {showAuthor && entry.meta.author && (
            <span className="text-[13px] text-ink-500">
              {author ? (
                <a
                  href={authorSlugPath(author)}
                  className="pointer-events-auto relative z-10 hover:text-ink-800 hover:underline"
                >
                  {entry.meta.author}
                </a>
              ) : (
                entry.meta.author
              )}
            </span>
          )}
        </div>
        <h3
          className={`mt-1 text-[16px] font-medium leading-[1.4] text-ink-900 ${
            isDeveloperPost ? 'font-mono' : ''
          }`}
        >
          {entry.meta.title}
        </h3>
      </div>
      {showImage && (
        <img
          src={entry.meta.image}
          alt={entry.meta.title}
          loading="lazy"
          className="pointer-events-none hidden h-16 w-28 shrink-0 rounded-lg border border-ink-100 object-cover sm:block"
        />
      )}
    </div>
  )
}

/** Faint monospace glyphs scattered behind the hero heading - a subtle nod to
 * the blog's developer-ish tone, static (no animation) so it stays cheap and
 * prerender-safe with zero motion to gate on reduced-motion. */
const BACKDROP_GLYPHS = [
  { glyph: '{ }', top: '8%', left: '6%', size: 34, rotate: -8 },
  { glyph: '//', top: '18%', left: '88%', size: 44, rotate: 6 },
  { glyph: '[ ]', top: '62%', left: '4%', size: 30, rotate: 5 },
  { glyph: '=>', top: '72%', left: '92%', size: 38, rotate: -4 },
  { glyph: '</>', top: '40%', left: '96%', size: 26, rotate: 10 },
]

function BlogHeroBackdrop() {
  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
      style={{
        background:
          'radial-gradient(ellipse 60% 50% at 15% 0%, rgba(91,141,239,0.06), transparent 60%)',
      }}
    >
      {BACKDROP_GLYPHS.map((g) => (
        <span
          key={g.glyph + g.top}
          className="absolute select-none font-mono text-ink-900/[0.035]"
          style={{
            top: g.top,
            left: g.left,
            fontSize: g.size,
            transform: `rotate(${g.rotate}deg)`,
          }}
        >
          {g.glyph}
        </span>
      ))}
    </div>
  )
}

function FilterBar({
  tabs,
  selected,
  onSelect,
}: {
  tabs: string[]
  selected: string
  onSelect: (c: string) => void
}) {
  return (
    <div className="mt-8 flex flex-wrap gap-2">
      {tabs.map((cat) => {
        const active = cat === selected
        return (
          <button
            key={cat}
            type="button"
            onClick={() => onSelect(cat)}
            aria-pressed={active}
            className={
              active
                ? 'rounded-full bg-ink-900 px-4 py-1.5 text-[13px] font-medium text-paper'
                : 'rounded-full border border-ink-200 px-4 py-1.5 text-[13px] font-medium text-ink-600 transition-colors hover:border-ink-300 hover:text-ink-900'
            }
          >
            {cat}
          </button>
        )
      })}
    </div>
  )
}

export function BlogIndexPage() {
  const posts = blogPosts()
  const [selected, setSelected] = useState('All')

  // Only surface category tabs that actually have posts, in canonical order.
  const present = new Set(posts.map((p) => p.meta.category).filter(Boolean) as string[])
  const tabs = ['All', ...CATEGORIES.filter((c) => present.has(c))]

  const visible = selected === 'All' ? posts : posts.filter((p) => p.meta.category === selected)
  const pinned =
    selected === 'All'
      ? PINNED.map((slug) => posts.find((p) => p.slug === slug)).filter((p): p is ContentEntry => !!p)
      : []
  const featured = pinned.length > 0 ? pinned : visible.slice(0, 6)
  const rest = visible.filter((p) => !featured.includes(p))

  return (
    <SiteLayout>
      <section className="relative overflow-hidden pt-28 pb-12 md:pt-32">
        <BlogHeroBackdrop />
        <div className="container-wide relative">
          <h1 className="display text-[34px] leading-[1.05] text-ink-900 md:text-[52px]">
            The LangWatch <span className="text-gradient">Blog</span>
          </h1>
          <p className="mt-5 max-w-[640px] text-[18px] leading-[1.6] text-ink-600">
            Engineering deep-dives, product updates, and field lessons on evaluating, testing,
            and observing AI agents in production.
          </p>
          <FilterBar tabs={tabs} selected={selected} onSelect={setSelected} />
        </div>
      </section>

      {featured.length > 0 && (
        <section className="pb-16">
          <div className="container-wide">
            <div className="grid grid-cols-1 gap-x-7 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
              {featured.map((p) => {
                const Card = CUSTOM_CARDS[p.slug] ?? FeaturedCard
                return <Card key={p.slug} entry={p} />
              })}
            </div>
          </div>
        </section>
      )}

      {rest.length > 0 && (
        <section className="pb-24">
          <div className="container-wide">
            <div className="text-[12.5px] font-semibold uppercase tracking-[0.16em] text-ink-400">
              {selected === 'All' ? 'More from the blog' : `More in ${selected}`}
            </div>
            <div className="mt-4 border-t border-ink-100">
              {rest.map((p) => (
                <ArchiveRow key={p.slug} entry={p} />
              ))}
            </div>
          </div>
        </section>
      )}

      {visible.length === 0 && (
        <section className="pb-24">
          <div className="container-wide text-[15px] text-ink-500">
            No posts in this category yet.
          </div>
        </section>
      )}
    </SiteLayout>
  )
}
